import React, { useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Stepper, Step, StepLabel, Box, Button, Typography } from "@mui/material";
import { designTokens } from "../../theme.ts";
import { EstimateDto, estimateResolver, defaultEstimateDto } from "../../contracts/estimates/types.ts";
import { useEstimateApi } from "../../hooks/useEstimateApi.ts";
import { useRefreshGuard } from "../../hooks/refreshGuard.tsx";
import { useProfile } from "../../hooks/useProfile.tsx";
import { usePopup } from "../../hooks/usePopup.ts";
import dayjs from "dayjs";

// 디자인 시스템 토큰 (홈/로그인/사이드바와 동일한 값)
const C = {
    surface: "#FFFFFF",
    border: "#E4E7EA",
    ink: "#171A1E",
    sub: "#6B7178",
    accent: "#E2451C",
    chipBg: "#F2F4F5",
};

const SANS = designTokens.fonts.sans;

export interface WizardStepConfig {
    label: string;
    render: (props: any) => React.ReactNode;
}

export interface EstimateWizardContainerProps {
    stepsConfig: WizardStepConfig[];
    defaultValues: Partial<EstimateDto>;
    companyInChargeIndex: number;
    getPreviewBlob: (data: EstimateDto, activeStep: number) => Promise<{ cover?: Blob, content?: Blob }>;
}

export default function EstimateWizardContainer({ stepsConfig, defaultValues, companyInChargeIndex, getPreviewBlob }: EstimateWizardContainerProps) {
    const [activeStep, setActiveStep] = useState(0);
    const [coverPdfBlob, setCoverPdfBlob] = useState<Blob | null>(null);
    const [loading, setLoading] = useState(false);
    const [contentPdfBlob, setContentPdfBlob] = useState<Blob | null>(null);
    const { createEstimate } = useEstimateApi();
    const { load } = useProfile();
    const { openDownloadPopup, openLoadPopup } = usePopup();

    const methods = useForm<EstimateDto>({
        defaultValues: {
            ...defaultEstimateDto,
            ...defaultValues,
            cover: { ...defaultEstimateDto.cover, ...(defaultValues.cover ?? {}) },
            content: { ...defaultEstimateDto.content, ...(defaultValues.content ?? {}) },
            id: crypto.randomUUID(),
        },
        mode: "onBlur",
        resolver: estimateResolver
    });
    const { setValue } = methods;

    const applyLoadedEstimate = (src: EstimateDto) => {
        const merged: EstimateDto = {
            ...defaultEstimateDto,
            ...src,
            companyInCharge: defaultValues.companyInCharge || src.companyInCharge,
            cover: { ...defaultEstimateDto.cover, ...src.cover },
            content: { ...defaultEstimateDto.content, ...src.content },
            id: crypto.randomUUID(),
            createdTimestamp: dayjs().tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        };
        methods.reset(merged, { keepDefaultValues: true });
        setCoverPdfBlob(null);
        setContentPdfBlob(null);
        setActiveStep(0);
        setLoading(false);
    };

    const onLoadPreviousPopup = () => {
        openLoadPopup({
            path: "/popup-load/estimate",
            windowName: "이전 견적서 불러오기",
            expectedType: "ESTIMATE_SELECTED",
            onReceive: (payload) => applyLoadedEstimate(payload as EstimateDto)
        });
    };

    const loadedOnce = useRef(false);
    useEffect(() => {
        if (loadedOnce.current) return;
        loadedOnce.current = true;
        let active = true;
        (async () => {
            try {
                const remote = await load();
                if (!active) return;
                if (remote.username) {
                    setValue("cover.sender", remote.username);
                    setValue("content.personInCharge", remote.position ? `${remote.username} ${remote.position}` : remote.username);
                }
                if (remote.phone) {
                    setValue("cover.senderMP", remote.phone);
                }
            } catch (e) {
                console.error("[/profile load] failed:", e);
            }
        })();
        return () => { active = false; };
    }, [load, setValue]);

    useRefreshGuard(methods, setCoverPdfBlob, setContentPdfBlob, setActiveStep, setLoading);

    const getPreview = async () => {
        try {
            const data = methods.getValues();
            const blobs = await getPreviewBlob(data, activeStep);
            if (blobs.cover !== undefined) setCoverPdfBlob(blobs.cover);
            if (blobs.content !== undefined) setContentPdfBlob(blobs.content);
        } catch (e) {
            alert((e as Error).message);
        }
    };

    const next = async () => {
        await methods.trigger("cover");
        if (activeStep === stepsConfig.length - 1) {
            const draftId = crypto.randomUUID();
            methods.setValue("id", draftId);
            const data = methods.getValues();
            setLoading(true);
            try {
                await createEstimate(data);
                sessionStorage.removeItem("draft-id");
            } catch {
                console.log("Fail to create estimate");
            }
        } else {
            setActiveStep((p) => p + 1);
        }
    };

    const back = () => setActiveStep((p) => p - 1);

    const onDownloadPopup = () => {
        const tempData = methods.getValues();
        openDownloadPopup({
            path: "/popup-download/estimate",
            key: `${tempData.id}`,
            payloadData: tempData,
            windowName: "견적서 파일 다운로드"
        });
    };

    const reset = () => {
        methods.reset({
            ...defaultEstimateDto,
            ...defaultValues,
            cover: { ...defaultEstimateDto.cover, ...(defaultValues.cover ?? {}) },
            content: { ...defaultEstimateDto.content, ...(defaultValues.content ?? {}) },
            id: crypto.randomUUID(),
        });
        setCoverPdfBlob(null);
        setContentPdfBlob(null);
        setActiveStep(0);
        setLoading(false);
    };

    return (
        <FormProvider {...methods}>
            <Box sx={{ width: "100%", fontFamily: SANS }}>
                {/* 페이지 헤더 — 제목 + 진행 단계 서브텍스트 + 우측 액션 */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: SANS,
                                fontWeight: 700,
                                fontSize: 22,
                                lineHeight: 1.3,
                                letterSpacing: "-0.01em",
                                color: C.ink,
                            }}
                        >
                            견적서 작성
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.5,
                                fontFamily: SANS,
                                fontSize: 14,
                                color: C.sub,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {activeStep + 1} / {stepsConfig.length} 단계 · {stepsConfig[activeStep]?.label}
                        </Typography>
                    </Box>

                    <Button variant="outlined" onClick={onLoadPreviousPopup}>
                        이전 견적서 내용 불러오기
                    </Button>
                </Box>

                {/* 스텝퍼 — 흰 서피스 카드 위에 배치 */}
                <Box
                    sx={{
                        bgcolor: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: "12px",
                        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
                        px: { xs: 2, md: 3 },
                        py: 2.5,
                        mb: 3,
                    }}
                >
                    <Stepper
                        activeStep={activeStep}
                        sx={{
                            "& .MuiStepLabel-label": {
                                fontFamily: SANS,
                                fontSize: 14,
                                fontWeight: 500,
                                color: C.sub,
                            },
                            "& .MuiStepLabel-label.Mui-active": { color: C.ink, fontWeight: 700 },
                            "& .MuiStepLabel-label.Mui-completed": { color: C.ink, fontWeight: 600 },
                            "& .MuiStepIcon-root": {
                                color: C.chipBg,
                                "& .MuiStepIcon-text": { fill: C.sub, fontWeight: 600 },
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                                color: C.accent,
                                "& .MuiStepIcon-text": { fill: "#FFFFFF" },
                            },
                            "& .MuiStepIcon-root.Mui-completed": { color: C.accent },
                            "& .MuiStepConnector-line": { borderColor: C.border },
                        }}
                    >
                        {stepsConfig.map((config) => (
                            <Step key={config.label}>
                                <StepLabel>{config.label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {stepsConfig[activeStep].render({
                    onNext: next,
                    onBack: back,
                    onPreview: getPreview,
                    onDownload: onDownloadPopup,
                    onReset: reset,
                    coverPdfBlob,
                    contentPdfBlob,
                    activeStep,
                    totalSteps: stepsConfig.length,
                    loading
                })}
            </Box>
        </FormProvider>
    );
}
